import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";
import { supabase } from "../lib/supabase";
import { stripAuthParamsFromUrl } from "../utils/authHelpers";

export const AuthContext = createContext(null);

const SUPPORT_DOMAIN = "audiohook.com";
const MASQUERADE_STORAGE_KEY = "cpa_ui_masquerade";
const CURRENT_ADVERTISER_STORAGE_KEY = "cpa_ui_current_advertiser";

// Fallback display name when the advertisers table name is unavailable.
function advertiserLabel(name, id) {
  return name || `Advertiser #${id}`;
}

function getStoredMasquerade() {
  try {
    const raw = sessionStorage.getItem(MASQUERADE_STORAGE_KEY);
    if (!raw) return { id: null, name: "" };
    const { advertiserId, name } = JSON.parse(raw);
    if (typeof advertiserId !== "number" && typeof advertiserId !== "string") return { id: null, name: "" };
    return { id: Number(advertiserId), name: name || "" };
  } catch {
    return { id: null, name: "" };
  }
}

function getStoredCurrentAdvertiserId() {
  try {
    const raw = localStorage.getItem(CURRENT_ADVERTISER_STORAGE_KEY);
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistCurrentAdvertiserId(id) {
  try {
    if (id == null) localStorage.removeItem(CURRENT_ADVERTISER_STORAGE_KEY);
    else localStorage.setItem(CURRENT_ADVERTISER_STORAGE_KEY, String(id));
  } catch {
    // Silently ignore — localStorage may be blocked in private mode.
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [userId, setUserId] = useState(null);
  const [userFirstName, setUserFirstName] = useState(null);

  // Each entry: { id: number, name: string, role: 'admin'|'member'|'viewer', billing_enabled: boolean, is_primary: boolean }
  const [advertisers, setAdvertisers] = useState([]);
  const [advertisersLoading, setAdvertisersLoading] = useState(false);
  const [advertisersLoaded, setAdvertisersLoaded] = useState(false);
  const [currentAdvertiserId, setCurrentAdvertiserIdState] = useState(() => getStoredCurrentAdvertiserId());

  // Read masquerade once so id and name come from the same parse.
  const [masqueradeAdvertiserId, setMasqueradeAdvertiserIdState] = useState(() => getStoredMasquerade().id);
  const [masqueradeAdvertiserName, setMasqueradeAdvertiserName] = useState(() => {
    // getStoredMasquerade is cheap (one sessionStorage read), called again here
    // so both pieces of state are initialised from a consistent snapshot.
    return getStoredMasquerade().name;
  });
  // Name cache for advertisers the user isn't a member of (masquerade targets).
  const [advertiserNameById, setAdvertiserNameById] = useState({});

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") stripAuthParamsFromUrl();
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    if (!supabase || !user?.email) {
      setUserId(null);
      setUserFirstName(null);
      setAdvertisers([]);
      setAdvertisersLoaded(false);
      return;
    }
    let cancelled = false;
    setAdvertisersLoading(true);
    (async () => {
      const { data: profile, error: profileError } = await supabase
        .from("users")
        .select("id, first_name")
        .eq("email", user.email)
        .maybeSingle();
      if (cancelled) return;
      if (profileError || !profile) {
        setUserId(null);
        setUserFirstName(null);
        setAdvertisers([]);
        setAdvertisersLoading(false);
        setAdvertisersLoaded(true);
        return;
      }
      setUserId(profile.id);
      setUserFirstName(profile.first_name ?? null);

      // Falls back to users.advertiser_id when user_advertisers doesn't exist yet
      // (migration 023 not yet applied to this environment).
      const { data: rows, error: membershipsError } = await supabase
        .from("user_advertisers")
        .select("advertiser_id, role, billing_enabled, is_primary, advertisers(id, name)")
        .eq("user_id", profile.id)
        .order("is_primary", { ascending: false })
        .order("advertiser_id", { ascending: true });
      if (cancelled) return;

      let list;
      if (membershipsError) {
        const { data: legacyProfile } = await supabase
          .from("users")
          .select("advertiser_id, advertisers(id, name)")
          .eq("email", user.email)
          .maybeSingle();
        if (cancelled) return;
        list = legacyProfile?.advertiser_id != null
          ? [{
              id: legacyProfile.advertiser_id,
              name: advertiserLabel(legacyProfile.advertisers?.name, legacyProfile.advertiser_id),
              role: "admin",
              billing_enabled: true,
              is_primary: true,
            }]
          : [];
      } else {
        list = (rows || []).map((r) => ({
          id: r.advertiser_id,
          name: advertiserLabel(r.advertisers?.name, r.advertiser_id),
          role: r.role,
          billing_enabled: !!r.billing_enabled,
          is_primary: !!r.is_primary,
        }));
      }
      setAdvertisers(list);
      setAdvertisersLoading(false);
      setAdvertisersLoaded(true);

      // Seed the name cache so masquerade lookups for the user's own advertisers
      // don't need an extra round-trip.
      setAdvertiserNameById((prev) => {
        const next = { ...prev };
        for (const a of list) next[a.id] = a.name;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  // Reconcile currentAdvertiserId once the membership list is known.
  // Gated on advertisersLoaded so a persisted selection is never clobbered
  // before the fetch completes.
  useEffect(() => {
    if (!advertisersLoaded || advertisersLoading) return;
    if (advertisers.length === 0) {
      if (currentAdvertiserId != null) {
        setCurrentAdvertiserIdState(null);
        persistCurrentAdvertiserId(null);
      }
      return;
    }
    const stillValid =
      currentAdvertiserId != null &&
      advertisers.some((a) => a.id === currentAdvertiserId);
    if (stillValid) return;
    const primary = advertisers.find((a) => a.is_primary) || advertisers[0];
    setCurrentAdvertiserIdState(primary.id);
    persistCurrentAdvertiserId(primary.id);
  }, [advertisers, advertisersLoading, advertisersLoaded, currentAdvertiserId]);

  // Identify clients in PostHog; fully opt out internal @audiohook.com users
  // so neither autocapture nor manual events leak through for internal sessions.
  // We use email as the distinct ID for cross-device consistency.
  useEffect(() => {
    if (!user?.email) return;
    if (user.email.toLowerCase().endsWith(`@${SUPPORT_DOMAIN}`)) {
      posthog.opt_out_capturing();
      return;
    }
    posthog.opt_in_capturing();
    posthog.identify(user.email, {
      email: user.email,
      ...(userId != null ? { user_id: String(userId) } : {}),
      ...(userFirstName ? { name: userFirstName } : {}),
    });
  }, [userId, user?.email, userFirstName]);

  const signIn = useCallback(async (email, options = {}) => {
    if (!supabase) return { error: { message: "Supabase not configured" } };
    const redirectTo = options.emailRedirectTo || window.location.origin;
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo },
    });
    return { data, error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) return { error: { message: "Supabase not configured" } };
    return supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    posthog.reset();
    setMasqueradeAdvertiserIdState(null);
    setMasqueradeAdvertiserName("");
    sessionStorage.removeItem(MASQUERADE_STORAGE_KEY);
    setCurrentAdvertiserIdState(null);
    persistCurrentAdvertiserId(null);
    await supabase.auth.signOut({ scope: "global" });
  }, []);

  const setCurrentAdvertiserId = useCallback((id) => {
    const n = id != null ? Number(id) : null;
    if (n != null && !advertisers.some((a) => a.id === n)) return;
    setCurrentAdvertiserIdState(n);
    persistCurrentAdvertiserId(n);
  }, [advertisers]);

  const setMasquerade = useCallback((advertiserId, name) => {
    const id = advertiserId != null ? Number(advertiserId) : null;
    const n = name || "";
    setMasqueradeAdvertiserIdState(id);
    setMasqueradeAdvertiserName(n);
    if (id != null) {
      sessionStorage.setItem(MASQUERADE_STORAGE_KEY, JSON.stringify({ advertiserId: id, name: n }));
    } else {
      sessionStorage.removeItem(MASQUERADE_STORAGE_KEY);
    }
  }, []);

  const clearMasquerade = useCallback(() => {
    setMasqueradeAdvertiserIdState(null);
    setMasqueradeAdvertiserName("");
    sessionStorage.removeItem(MASQUERADE_STORAGE_KEY);
  }, []);

  const isSupportUser =
    !!user?.email && user.email.toLowerCase().endsWith(`@${SUPPORT_DOMAIN}`);

  const effectiveAdvertiserId =
    masqueradeAdvertiserId != null ? masqueradeAdvertiserId : currentAdvertiserId;

  // Lazy-fetch the display name for masquerade targets that aren't in the
  // user's own membership list. Keyed on the primitive id to avoid re-running
  // every time an unrelated name is added to the cache object.
  const knownEffectiveName = effectiveAdvertiserId != null
    ? advertiserNameById[effectiveAdvertiserId]
    : undefined;
  const fetchedNameRef = useRef(null);
  useEffect(() => {
    if (!supabase || effectiveAdvertiserId == null || knownEffectiveName) return;
    if (fetchedNameRef.current === effectiveAdvertiserId) return;
    fetchedNameRef.current = effectiveAdvertiserId;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("advertisers")
        .select("id, name")
        .eq("id", effectiveAdvertiserId)
        .maybeSingle();
      if (!cancelled && !error && data?.name != null) {
        setAdvertiserNameById((prev) => ({ ...prev, [data.id]: data.name }));
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveAdvertiserId, knownEffectiveName]);

  const effectiveAdvertiserName =
    masqueradeAdvertiserId != null
      ? (masqueradeAdvertiserName || advertiserNameById[masqueradeAdvertiserId] || null)
      : (effectiveAdvertiserId != null ? advertiserNameById[effectiveAdvertiserId] : null);

  const currentMembership = effectiveAdvertiserId != null
    ? advertisers.find((a) => a.id === effectiveAdvertiserId) || null
    : null;

  const canViewAdvertiserData = isSupportUser || !!currentMembership;
  const canManageAdvertiserData =
    isSupportUser || (currentMembership?.role === "admin" || currentMembership?.role === "member");
  const canManageBilling = isSupportUser || !!currentMembership?.billing_enabled;
  const isAdvertiserAdmin = isSupportUser || currentMembership?.role === "admin";

  const updateFirstName = useCallback(async (name) => {
    if (!supabase || !user?.email) return { error: { message: "Not authenticated" } };
    const trimmed = name.trim();
    const [authRes] = await Promise.all([
      supabase.auth.updateUser({ data: { full_name: trimmed } }),
      supabase.from("users").update({ first_name: trimmed }).eq("email", user.email),
    ]);
    if (!authRes.error) setUserFirstName(trimmed);
    return authRes;
  }, [user?.email]);

  const value = useMemo(() => ({
    session,
    user,
    userId,
    userFirstName,
    loading,
    signIn,
    signInWithGoogle,
    signOut,
    updateFirstName,
    isConfigured: !!supabase,
    isSupportUser,
    advertisers,
    advertisersLoading,
    currentAdvertiserId,
    setCurrentAdvertiserId,
    effectiveAdvertiserId,
    effectiveAdvertiserName,
    masqueradeAdvertiserId,
    masqueradeAdvertiserName,
    setMasquerade,
    clearMasquerade,
    currentMembership,
    canViewAdvertiserData,
    canManageAdvertiserData,
    canManageBilling,
    isAdvertiserAdmin,
  }), [
    session, user, userId, userFirstName, loading,
    signIn, signInWithGoogle, signOut, updateFirstName,
    isSupportUser,
    advertisers, advertisersLoading, currentAdvertiserId, setCurrentAdvertiserId,
    effectiveAdvertiserId, effectiveAdvertiserName,
    masqueradeAdvertiserId, masqueradeAdvertiserName,
    setMasquerade, clearMasquerade,
    currentMembership,
    canViewAdvertiserData, canManageAdvertiserData, canManageBilling, isAdvertiserAdmin,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
